import { useSelector } from 'react-redux'
import { Workspace } from '@weblite/ts-action/workspace'
import {
  getWorkspaceByIdView,
  getWorkspacesView,
} from '$logic/workspace/storage'
import { DEFAULT_WORKSPACE_ID } from '$logic/workspace/constant'
import { getOwnerWorkspaces } from './service'
import { useCurrentWorkSpaceId } from '$comp/main/react-hook'
import { useRoom } from '$logic/room'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { usePinnedRoomIdsAndWorkspaces } from '$logic/user-preferences/react-hook'
import { useClientUser } from '$logic/client-user'
import { useAsyncFunction } from '$helper/functions/async-function.react-hook'

export function useWorkspaces() {
  return useSelector(getWorkspacesView)
}

export function useRenderWorkspaces() {
  return useWorkspacesIds().length > 0
}

export function useWorkspacesIds() {
  const workspaces = useWorkspaces()
  return Object.keys(workspaces)
}

export function useWorkspaceById(id: string) {
  return useSelector(getWorkspaceByIdView(id))
}

export function useCurrentWorkspace() {
  const id = useCurrentWorkSpaceId() as string
  return useSelector(getWorkspaceByIdView(id)) as Workspace | undefined
}

export function useIsDefaultWorkspace() {
  const currentWorkspaceId = useCurrentWorkSpaceId()
  return currentWorkspaceId === DEFAULT_WORKSPACE_ID
}

export function useWorkspaceColor(workspaceId: string) {
  const workspace = useWorkspaceById(workspaceId)
  return workspace?.color
}

export function useWorkspaceColorByRoomId(roomId: string) {
  const room = useRoom(roomId)
  const workspaceColor = useConditionalHook(!!room?.workspaceId, {
    hook: useWorkspaceColor,
    hookArgs: room?.workspaceId as string,
    defaultValue: undefined,
  })
  return workspaceColor
}

export function usePinnedWorkspaceIds() {
  const workspaces = useWorkspaces()
  const pinnedRoomIdsAndWorkspaces = usePinnedRoomIdsAndWorkspaces()
  return pinnedRoomIdsAndWorkspaces.filter(id => !!workspaces[id])
}

export function useClientUserWorkspaces() {
  return useAsyncFunction(() => {
    return getOwnerWorkspaces({})
  }, [])
}

export function useIsClientUserWorkspaceOwner(workspaceId: string) {
  const workspace = useWorkspaceById(workspaceId)
  const clientUserId = useClientUser()?.id
  return !!workspace?.ownerId && workspace?.ownerId === clientUserId
}
